import * as React from "react";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import styled, { css, useTheme } from "styled-components";
import { forceSimulation, forceLink, forceManyBody, forceCenter, forceCollide } from "d3-force";
import { makeVisualization, retrieveChartData } from "views/components/aggregationbuilder/AggregationBuilder";
import useMapKeys from "views/components/visualizations/useMapKeys";
import extractLeafPaths from "views/components/visualizations/utils/extractLeafPaths";
import usePlotOnClickPopover from "views/components/visualizations/hooks/usePlotOnClickPopover";
import NetworkVisualizationConfig from "views/logic/aggregationbuilder/visualizations/NetworkVisualizationConfig";
import buildGraph from "./buildGraph";
import edgeColorScale from "./edgeColorScale";
import normalizeEdgeValue from "./normalizeEdgeValue";
import networkOnClickPopover from "./networkOnClickPopover";
import GenericPlot from "../GenericPlot";
const Container = styled.div(
  ({ $height, $width }) => css`
    height: ${$height ? `${$height}px` : "100%"};
    width: ${$width ? `${$width}px` : "100%"};
  `
);
const EmptyState = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    padding: ${theme.spacings.md};
    color: ${theme.colors.text.secondary};
    text-align: center;
  `
);
const LAYOUT_ITERATIONS = 500;
const NODE_RADIUS = 75;
const EDGE_HIT_SAMPLES = 9;
const EDGE_WIDTH = 2;
const runLayout = (nodeCount, links) => {
  const simNodes = Array.from({ length: nodeCount }, (_, i) => ({ id: i }));
  const simLinks = links.map((l) => ({ source: l.source, target: l.target }));
  const simulation = forceSimulation(simNodes).force(
    "link",
    forceLink(simLinks).id((d) => d.id).distance(200)
  ).force("charge", forceManyBody().strength(-900)).force("collide", forceCollide(NODE_RADIUS)).force("center", forceCenter(0, 0)).stop();
  for (let i = 0; i < LAYOUT_ITERATIONS; i += 1) simulation.tick();
  return simNodes;
};
const radialTextPosition = (x, y) => {
  if (x === 0 && y === 0) return "top center";
  const angle = Math.atan2(y, x);
  const slice = Math.round(angle / Math.PI * 4);
  switch ((slice % 8 + 8) % 8) {
    case 0:
      return "middle right";
    case 1:
      return "top right";
    case 2:
      return "top center";
    case 3:
      return "top left";
    case 4:
      return "middle left";
    case 5:
      return "bottom left";
    case 6:
      return "bottom center";
    case 7:
      return "bottom right";
    default:
      return "top center";
  }
};
const PLOT_CONFIG = { scrollZoom: true, doubleClick: "reset" };
const RANGE_PADDING = 0.2;
const paddedRange = (values) => {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const pad = span * RANGE_PADDING;
  return [min - pad, max + pad];
};
const buildLayout = (width, height, positions) => {
  const base = {
    margin: { t: 20, b: 70, l: 20, r: 20 },
    dragmode: "pan",
    xaxis: { visible: false, zeroline: false, showgrid: false, fixedrange: false },
    yaxis: { visible: false, zeroline: false, showgrid: false, fixedrange: false },
    showlegend: false,
    hovermode: "closest",
    width,
    height
  };
  if (!positions) {
    return {
      ...base,
      xaxis: { ...base.xaxis, autorange: true },
      yaxis: { ...base.yaxis, autorange: true }
    };
  }
  return {
    ...base,
    xaxis: { ...base.xaxis, range: paddedRange(positions.xs) },
    yaxis: { ...base.yaxis, range: paddedRange(positions.ys) }
  };
};
const NetworkGraphVisualization = makeVisualization(({ config, data, height, width }) => {
  var _a;
  const { t: translate } = useTranslation("aggregation");
  const rows = retrieveChartData(data);
  const mapKeys = useMapKeys();
  const theme = useTheme();
  const { onChartClick, initializeGraphDivRef, popover } = usePlotOnClickPopover({ ...networkOnClickPopover, config });
  const visualizationConfig = (_a = config.visualizationConfig) != null ? _a : NetworkVisualizationConfig.empty();
  const plot = useMemo(() => {
    var _a2;
    const rowFields = config.rowPivots.flatMap((pivot) => pivot.fields);
    const columnFields = config.columnPivots.flatMap((pivot) => pivot.fields);
    const allFields = [...rowFields, ...columnFields];
    if (allFields.length < 2 || !rows) return null;
    const metric = (_a2 = config.series) == null ? void 0 : _a2[0];
    const paths = extractLeafPaths(rows, columnFields.length, metric == null ? void 0 : metric.effectiveName);
    if (paths.length === 0) return null;
    const { nodes, edges } = buildGraph(paths, allFields);
    if (edges.length === 0) return null;
    const positions = runLayout(nodes.length, edges);
    const values = edges.map((edge) => edge.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const scale = edgeColorScale(visualizationConfig.colorScale);
    const edgeTraces = [];
    const hitX = [];
    const hitY = [];
    const hitCustomData = [];
    edges.forEach((edge) => {
      const s = positions[edge.source];
      const t = positions[edge.target];
      if (s.x === void 0 || s.y === void 0 || t.x === void 0 || t.y === void 0) return;
      const sourceNode = nodes[edge.source];
      const targetNode = nodes[edge.target];
      const cd = {
        source: {
          customdata: { field: sourceNode.field, value: sourceNode.value },
          label: sourceNode.label
        },
        target: {
          customdata: { field: targetNode.field, value: targetNode.value },
          label: targetNode.label
        },
        value: edge.value
      };
      const normalized = normalizeEdgeValue(edge.value, minValue, maxValue);
      const position = visualizationConfig.reverseScale ? 1 - normalized : normalized;
      edgeTraces.push({
        type: "scatter",
        mode: "lines",
        x: [s.x, t.x],
        y: [s.y, t.y],
        line: { width: EDGE_WIDTH, color: scale(position).hex() },
        customdata: [cd, cd],
        hoverinfo: "none",
        showlegend: false
      });
      for (let i = 1; i <= EDGE_HIT_SAMPLES; i += 1) {
        const f = i / (EDGE_HIT_SAMPLES + 1);
        hitX.push(s.x + f * (t.x - s.x));
        hitY.push(s.y + f * (t.y - s.y));
        hitCustomData.push(cd);
      }
    });
    const textColor = theme.colors.text.primary;
    const hitTrace = {
      type: "scatter",
      mode: "markers",
      x: hitX,
      y: hitY,
      // Invisible (opacity 0) but still rendered as points, so they remain hit-detectable.
      marker: { size: 10, opacity: 0, color: theme.colors.text.secondary },
      customdata: hitCustomData,
      hoverinfo: "none",
      showlegend: false
    };
    const displayLabels = nodes.map((n) => {
      var _a3;
      return String((_a3 = mapKeys(n.value, n.field)) != null ? _a3 : n.label);
    });
    const xs = positions.map((p) => {
      var _a3;
      return (_a3 = p.x) != null ? _a3 : 0;
    });
    const ys = positions.map((p) => {
      var _a3;
      return (_a3 = p.y) != null ? _a3 : 0;
    });
    const nodeTrace = {
      type: "scatter",
      mode: "markers+text",
      x: xs,
      y: ys,
      text: displayLabels,
      textposition: positions.map((p) => {
        var _a3, _b;
        return radialTextPosition((_a3 = p.x) != null ? _a3 : 0, (_b = p.y) != null ? _b : 0);
      }),
      textfont: { color: textColor },
      cliponaxis: false,
      marker: {
        size: 14,
        color: nodes.map((n) => n.degree),
        colorscale: visualizationConfig.colorScale,
        reversescale: visualizationConfig.reverseScale,
        showscale: true,
        colorbar: {
          title: { text: "Connections", font: { color: textColor }, side: "top" },
          orientation: "h",
          x: 0.5,
          xanchor: "center",
          y: -0.05,
          yanchor: "top",
          len: 0.5,
          thickness: 10,
          tickfont: { color: textColor }
        },
        line: { width: 1.5, color: theme.colors.text.primary }
      },
      customdata: nodes.map((n) => ({ field: n.field, value: n.value })),
      hovertemplate: "%{text}<br>Connections: %{marker.color}<extra></extra>",
      showlegend: false
    };
    return { traces: [...edgeTraces, hitTrace, nodeTrace], xs, ys };
  }, [config, mapKeys, rows, theme, visualizationConfig]);
  const layout = useMemo(() => buildLayout(width, height, plot), [width, height, plot]);
  return /* @__PURE__ */ React.createElement(Container, { $height: height, $width: width }, plot ? /* @__PURE__ */ React.createElement(
    GenericPlot,
    {
      chartData: plot.traces,
      layout,
      config: PLOT_CONFIG,
      onClickMarker: onChartClick,
      onInitialized: initializeGraphDivRef
    }
  ) : /* @__PURE__ */ React.createElement(EmptyState, null, translate("vis_network.no_connections")), popover);
}, "network");
NetworkGraphVisualization.displayName = "NetworkGraphVisualization";
export default NetworkGraphVisualization;
